import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Outlet } from "react-router-dom";
import { Box, Drawer, useTheme, useMediaQuery } from "@mui/material";
import { logout } from "../redux/slices/userSlice";
import { fetchNotifications } from "../redux/slices/notificationSlice";
import { useSocket } from "../contexts/SocketContext";
import AdminSidebar from "../components/admin/AdminSidebar";
import TopBar from "../components/layout/TopBar";
import UserProfileMenu from "../components/layout/UserProfileMenu";
import NotificationsMenu from "../components/layout/NotificationsMenu";
import { LAYOUT } from "../theme/constants";
import { getPagePadding } from "../theme/responsive";

const AdminLayout = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const _isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const userState = useSelector((state) => state.user);
  const { unreadCount } = useSelector((state) => state.notification);
  const { socket } = useSocket();

  // Fetch notifications on mount
  useEffect(() => {
    if (userState?.isAuthenticated) {
      dispatch(fetchNotifications({ limit: 20 }));
      const interval = setInterval(() => {
        dispatch(fetchNotifications({ limit: 20 }));
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [dispatch, userState?.isAuthenticated]);

  // Socket notification refresh
  useEffect(() => {
    if (!socket || !userState?.isAuthenticated) return;

    const refreshNotifications = () => {
      dispatch(fetchNotifications({ limit: 20 }));
    };

    socket.on("site_status", refreshNotifications);
    socket.on("new_notification", refreshNotifications);

    return () => {
      socket.off("site_status", refreshNotifications);
      socket.off("new_notification", refreshNotifications);
    };
  }, [socket, dispatch, userState?.isAuthenticated]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    setTimeout(() => navigate("/auth"), 100);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      {/* Top Bar */}
      <TopBar
        drawerWidth={LAYOUT.sidebarWidth}
        user={userState?.user}
        notificationCount={unreadCount}
        onMenuClick={handleDrawerToggle}
        onNotificationClick={(e) => setNotificationAnchorEl(e.currentTarget)}
        onProfileClick={(e) => setProfileAnchorEl(e.currentTarget)}
      />

      {/* Notifications Menu */}
      <NotificationsMenu
        anchorEl={notificationAnchorEl}
        onClose={() => setNotificationAnchorEl(null)}
      />

      {/* Profile Menu */}
      <UserProfileMenu
        anchorEl={profileAnchorEl}
        onClose={() => setProfileAnchorEl(null)}
        onLogout={handleLogout}
      />

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: LAYOUT.sidebarWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: LAYOUT.sidebarWidth,
            },
          }}
        >
          <AdminSidebar
            user={userState?.user}
            isMobile={true}
            onNavigate={handleDrawerToggle}
          />
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: LAYOUT.sidebarWidth,
            },
          }}
          open
        >
          <AdminSidebar user={userState?.user} isMobile={false} />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: getPagePadding(),
          width: { md: `calc(100% - ${LAYOUT.sidebarWidth}px)` },
          mt: `${LAYOUT.topBarHeight}px`,
          minHeight: "100vh",
          transition: "all 0.15s ease",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
